import { useContext, useEffect, useState } from "react";
import AuthContext from "../Context/AuthProvider";
import {
  addPrescription,
  getPrescription,
  removePrescription,
  updatePrescription,
} from "../API/PrescriptionApi";
import { getServiceDetails } from "../API/LinkedServicesApi";
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Card, CircularProgress, FormControl, FormLabel, Grid2, Slider, TextField, Tooltip, Typography } from "@mui/material";
import { useSnackbar } from "../Utility/UserMessageContext";
import { Checkbox, FormControlLabel, FormGroup } from "@mui/material";
import { MedicalHistoryConditions, PhysioTreatmentMethods, SelectableMedicalHistoryConditions, SelectablePainType } from "../Utility/Constants";
import { GridExpandMoreIcon } from "@mui/x-data-grid";
import dayjs from "dayjs";
import PhysiotherapyPrescriptionReadOnly from "./PhysiotherapyPrescriptionReadOnly";
import { formatAge, showLoadingImage } from "../Utility/Utility";

function PhysiotherapyPrescriptionForm({ serviceId, mode, prescriptionId, onRemovePrescription, onSavePrescription }) {
  const [errorMessage, setErrorMessage] = useState();
  const { auth } = useContext(AuthContext);
  const [prescriptionStatus, setPrescriptionStatus] = useState();
  const [currentService, setCurrentService] = useState();
  const [isSaving, setIsSaving] = useState(false);
  
  const [complaint, setComplaint] = useState();
  const [medicalHistoryNotes, setMedicalHistoryNotes] = useState();
  const [medicalHistoryConditions, setMedicalHistoryConditions] = useState(SelectableMedicalHistoryConditions);
  const [surgeryDetails, setSurgeryDetails] = useState();
  const [painLevel, setPainLevel] = useState();
  const [painTypes, setPainTypes] = useState(SelectablePainType);
  const [assessmentAttributes, setAssessmentAttributes] = useState();
  const [posture, setPosture] = useState();
  const [rangeOfMotion, setRangeOfMotion] = useState();
  const [muscleStrength, setMuscleStrength] = useState();
  const [muscleTightness, setMuscleTightness] = useState();
  const [specialTests, setSpecialTests] = useState();
  const [treatmentPlan, setTreatmentPlan] = useState();
  const [dosDonts, setDosDonts] = useState();
  const [suggestedSessions, setSuggestedSessions] = useState();
  const [shortTermTreatmentGoals, setShortTermTreatmentGoals] = useState();
  const [longTermTreatmentGoals, setLongTermTreatmentGoals] = useState();
  const [treatmentMethods, setTreatmentMethods] = useState(PhysioTreatmentMethods);
  const [prescriptionToBeEdited, setPrescriptionToBeEdited] = useState();
  const [currentPrescriptionId, setCurrentPrescriptionId] = useState(prescriptionId);
  const [readOnly, setReadOnly] = useState(false);

  const {showSnackbar} = useSnackbar();

  useEffect(() => {
    const loadService = async () => {
      if (auth != null && serviceId != null) {
        setIsSaving(true); // Set loading state when fetching service details
        try {
          var result = await getServiceDetails(auth, serviceId);
          if (result?.isSuccess) {
            setCurrentService(result?.data);
          } else {
            showSnackbar(result?.data, "error");
          }
        } catch (error) {
          showSnackbar("Error loading service details: " + error.message, "error");
        } finally {
          // Only set loading to false if we're not also loading the prescription
          if (currentPrescriptionId == null) {
            setIsSaving(false);
          }
        }
      }
    };

    const loadPrescription = async () => {
      if (auth != null && currentPrescriptionId != null) {
        setIsSaving(true);
        try {
          var result = await getPrescription(auth, currentPrescriptionId);
          if (result.isSuccess) {
            var prescription = result?.data;
            setPrescriptionToBeEdited(prescription);
            setPrescriptionStatus(prescription?.status);
            
            // If status is "Final", set readOnly to true
            if (prescription?.status === "Final") {
              setReadOnly(true);
            }

            var detailedPrescriptionJson = JSON.parse(
              prescription?.detailedPrescription
            );
            if (prescription?.detailedPrescription) {
              setComplaint(detailedPrescriptionJson?.complaint);
              setMedicalHistoryConditions(detailedPrescriptionJson?.medicalHistoryConditions);
              setMedicalHistoryNotes(detailedPrescriptionJson?.medicalHistoryNotes);
              setSurgeryDetails(detailedPrescriptionJson?.surgeryDetails);
              setPainLevel(detailedPrescriptionJson?.painLevel);
              setPainTypes(detailedPrescriptionJson?.painTypes);
              setRangeOfMotion(detailedPrescriptionJson?.rangeOfMotion);
              setMuscleStrength(detailedPrescriptionJson?.muscleStrength);
              setMuscleTightness(detailedPrescriptionJson?.muscleTightness);
              setSpecialTests(detailedPrescriptionJson?.specialTests);
              setTreatmentPlan(detailedPrescriptionJson?.treatmentPlan);
              setDosDonts(detailedPrescriptionJson?.dosDonts);
              setSuggestedSessions(detailedPrescriptionJson?.suggestedSessions);
              setShortTermTreatmentGoals(detailedPrescriptionJson?.shortTermTreatmentGoals);
              setLongTermTreatmentGoals(detailedPrescriptionJson?.longTermTreatmentGoals);
              setTreatmentMethods(detailedPrescriptionJson?.treatmentMethods);
            } else {
              setComplaint("");
              setMedicalHistoryConditions(SelectableMedicalHistoryConditions);
              setMedicalHistoryNotes("");
              setSurgeryDetails("");
              setPainLevel(0);
              setPainTypes(SelectablePainType);
              setRangeOfMotion("");
              setMuscleStrength("");
              setMuscleTightness("");
              setSpecialTests("");
              setTreatmentPlan("");
              setDosDonts("");
              setSuggestedSessions(0);
              setShortTermTreatmentGoals("");
              setLongTermTreatmentGoals("");
              setTreatmentMethods(PhysioTreatmentMethods);
            }
          } else {
            showSnackbar(result?.data, "error");
          }
        } catch (error) {
          showSnackbar("Error loading prescription: " + error.message, "error");
        } finally {
          setIsSaving(false);
        }
      }
    };
    
    if (currentPrescriptionId) {
      loadPrescription();
      loadService();
    } else {
      loadService();
    }
  }, [auth, serviceId, currentPrescriptionId]);

  const savePrescription = async (status) => {
    setIsSaving(true);
    
    var prescription = {};

    prescription.complaint = complaint;
    prescription.medicalHistoryConditions = medicalHistoryConditions;
    prescription.medicalHistoryNotes = medicalHistoryNotes;
    prescription.surgeryDetails = surgeryDetails;
    prescription.painLevel = painLevel;
    prescription.painTypes = painTypes;
    prescription.rangeOfMotion = rangeOfMotion;
    prescription.muscleStrength = muscleStrength;
    prescription.muscleTightness = muscleTightness;
    prescription.specialTests = specialTests;
    prescription.treatmentPlan = treatmentPlan;
    prescription.dosDonts = dosDonts;
    prescription.suggestedSessions = suggestedSessions;
    prescription.shortTermTreatmentGoals = shortTermTreatmentGoals;
    prescription.longTermTreatmentGoals = longTermTreatmentGoals;
    prescription.treatmentMethods = treatmentMethods;
    prescription.status = status;
    var result = {};
    
    try {
      if (!currentPrescriptionId) {
        result = await addPrescription(auth, prescription, currentService?.id, "Physiotherapy");
        setCurrentPrescriptionId(result?.data);
      } else {
        result = await updatePrescription(
          auth,
          prescription,
          currentPrescriptionId
        );
      }
      
      if (result.isSuccess) {
        // If status is set to "Final", update readOnly state
        if (status === "Final") {
          setReadOnly(true);
        }
        
        onSavePrescription(serviceId, currentPrescriptionId || result?.data, prescription.status);
        showSnackbar("The prescription has been saved successfully.", "success");
      } else {
        showSnackbar(result?.data, "error");
      }
    } catch (error) {
      showSnackbar("Error saving prescription: " + error.message, "error");
    } finally {
      setIsSaving(false);
    }
  };

  const onRemovePrescriptionClick = async () => {
    if (auth != null && currentPrescriptionId != null) {
      setIsSaving(true);
      try {
        var result = await removePrescription(auth, currentPrescriptionId);
        if (result?.isSuccess) {
          onRemovePrescription(serviceId, currentPrescriptionId);
        } else {
          showSnackbar(result?.data, "error");
        }
      } catch (error) {
        showSnackbar("Error removing prescription: " + error.message, "error");
      } finally {
        setIsSaving(false);
      }
    }
  };

  const [othersChecked, setOthersChecked] = useState();

  const handleOthersCheck = (newConditions) => {
    const othersIndex = newConditions.findIndex(
      (condition) => condition.value === "Others"
    );
    if (othersIndex !== -1) {
      setOthersChecked(newConditions[othersIndex].selected);
    }
  }

  // If readOnly or status is "Final", render the ReadOnly component
  if (readOnly || prescriptionStatus === "Final") {
    return <PhysiotherapyPrescriptionReadOnly serviceId={serviceId} prescriptionId={currentPrescriptionId} />;
  }

  // Editable form view
  return (
    <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
      {/* Loading Overlay using only isSaving state */}
      
        {isSaving && (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(216, 214, 214, 0.4)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 1301, // One level above Dialog
      }}
    >
      {showLoadingImage()}
    </div>
  )}

      <Grid2 container size={12} sx={{ marginX: "10px", marginY: "10px" }}>
        <Grid2 container size={12}>
          <Grid2 container size={4} spacing={1} alignContent="center">
            {prescriptionStatus === "Draft" && (
              <>
                <Tooltip title="Delete Prescription">
                  <Button
                    variant="contained"
                    size="small"
                    color="secondary"
                    onClick={onRemovePrescriptionClick}
                    disabled={isSaving}
                  >
                    Delete
                  </Button>
                </Tooltip>
              </>
            )}
            <Tooltip title="Save Prescription as Draft">
              <Button
                variant="contained"
                size="small"
                color="secondary"
                onClick={() => savePrescription("Draft")}
              >
                Save
              </Button>
            </Tooltip>
            <Tooltip title="Finalize Prescription">
              <Button
                variant="contained"
                size="small"
                color="secondary"
                onClick={() => savePrescription("Final")}
              >
                Finalize
              </Button>
            </Tooltip>
          </Grid2>
        </Grid2>

        <Grid2 container size={12}>
          <Grid2
            size={{ sx: 12, sm: 12, md: 12, lg: 9, xl: 9 }}
            sx={{ paddingTop: "10px" }}
          >
            <Card
              variant="outlined"
              fullWidth
              sx={{ width: "100%", padding: "10px" }}
            >
              <Grid2 container spacing={1} size={12} sx={{ maxHeight: "calc(100vh - 200px)", overflowY: "auto"}}>
                <Grid2 container size={12}>
                  <Grid2 size={4}>
                    <Grid2 size={12}>
                      <Typography sx={{textDecoration: 'underline'}}>Patient Name</Typography>
                    </Grid2>
                    <Grid2 size={12}>
                      <Typography>
                        {currentService?.patientPrefix +
                          " " +
                          currentService?.patientFirstName +
                          " " +
                          currentService?.patientLastName}
                      </Typography>
                    </Grid2>
                  </Grid2>
                  <Grid2 size={4}>
                    <Grid2 size={12}>
                      <Typography sx={{textDecoration: 'underline'}}>Age and Gender</Typography>
                    </Grid2>
                    <Grid2 size={12}>
                      <Typography>
                         {formatAge(currentService?.patientAgeInYears)} and{" "}
                                                    {currentService?.patientGender}
                      </Typography>
                    </Grid2>
                  </Grid2>
                  <Grid2 size={4}>
                    <Grid2 size={12}>
                      <Typography sx={{textDecoration: 'underline'}}>MRN</Typography>
                    </Grid2>
                    <Grid2 size={12}>
                      <Typography>{currentService?.patientMRN}</Typography>
                    </Grid2>
                  </Grid2>
                </Grid2>
                <Grid2 container size={12}>
                  <Grid2 size={4}>
                    <Grid2 size={12}>
                      <Typography sx={{textDecoration: 'underline'}}>Visit Id</Typography>
                    </Grid2>
                    <Grid2 size={12}>
                      <Typography>{currentService?.visitDisplayId}</Typography>
                    </Grid2>
                  </Grid2>
                  <Grid2 size={4}>
                    <Grid2 size={12}>
                      <Typography sx={{textDecoration: 'underline'}}>Visit Type</Typography>
                    </Grid2>
                    <Grid2 size={12}>
                      <Typography></Typography>
                    </Grid2>
                  </Grid2>
                  <Grid2 size={4}>
                    <Grid2 size={12}>
                      <Typography sx={{textDecoration: 'underline'}}>Date Time</Typography>
                    </Grid2>
                    <Grid2 size={12}>
                      <Typography>
                        {dayjs().format("DD-MMM-YYYY hh:mm A")}
                      </Typography>
                    </Grid2>
                  </Grid2>
                </Grid2>

                <Accordion defaultExpanded variant="outlined" sx={{ width: "100%", maxWidth: "100%" }}>
                  <AccordionSummary
                    expandIcon={<GridExpandMoreIcon />}
                    aria-controls="panel1-content"
                    id="panel1-header"
                  >
                    <Typography component="span">
                      Complaint and Medical History
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ padding: "8px", position: "relative" }}>
                    <Grid2 container spacing={3} size={12} sx={{justifyContent:'center', paddingX:'10px'}}>
                      <Grid2 size={12}>
                        <TextField
                          id="outlined-multiline-static"
                          label="Chief Complaint"
                          multiline
                          rows={4}
                          fullWidth
                          value={complaint}
                          onChange={(e) => setComplaint(e.target.value)}
                          slotProps={{ inputLabel: { shrink: complaint?true:false} }}
                        />
                      </Grid2>
                      <Grid2 size={12}>
                        <FormControl>
                          <FormLabel component="legend">
                            Medical History (Select applicable conditions)
                          </FormLabel>
                          <FormGroup row> 
                            {medicalHistoryConditions?.map(
                              (condition, index) => (
                                <FormControlLabel
                                  key={index}
                                  control={
                                    <Checkbox
                                      checked={condition.selected}
                                      onChange={(e) => {
                                        const newConditions = [
                                          ...medicalHistoryConditions,
                                        ];
                                        newConditions[index].selected =
                                          e.target.checked;
                                        setMedicalHistoryConditions(newConditions);
                                      }}
                                    />
                                  }
                                  label={condition.displayValue}
                                />
                              )
                            )}
                          </FormGroup>
                        </FormControl>
                      </Grid2>
                      <Grid2 size={12}>
                        <TextField
                          id="outlined-multiline-static"
                          label="Medical History Notes"
                          multiline
                          rows={4}
                          fullWidth
                          value={medicalHistoryNotes}
                          onChange={(e) => setMedicalHistoryNotes(e.target.value)}
                          slotProps={{ inputLabel: { shrink: medicalHistoryNotes?true:false} }}
                        />
                      </Grid2>
                      <Grid2 size={12}>
                        <TextField
                          id="outlined-multiline-static"
                          label="Surgery Details"
                          multiline
                          rows={4}
                          fullWidth
                          value={surgeryDetails}
                          onChange={(e) => setSurgeryDetails(e.target.value)}
                          slotProps={{ inputLabel: { shrink: surgeryDetails?true:false} }}
                        />
                      </Grid2>
                    </Grid2>
                  </AccordionDetails>
                </Accordion>

                <Accordion defaultExpanded variant="outlined" sx={{ width: "100%", maxWidth: "100%" }}>
                  <AccordionSummary
                    expandIcon={<GridExpandMoreIcon />}
                    aria-controls="panel2-content"
                    id="panel2-header"
                  >
                    <Typography component="span">Assessment</Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ padding: "8px", position: "relative" }}>
                    <Grid2 container spacing={3} size={12} sx={{justifyContent:'center', paddingX:'10px'}}>
                      <Grid2 size={12}>
                        <Typography>Pain Level</Typography>
                      </Grid2>
                      <Grid2 container size={10} >
                        <Slider 
                          value={Number(painLevel)} 
                          step={1} 
                          min={0} 
                          max={10} 
                          valueLabelDisplay="auto" 
                          onChange={(e) => setPainLevel(Number(e.target.value))}
                        />
                      </Grid2>
                     
                      <Grid2 size={12}>
                        <FormControl>
                          <FormLabel component="legend">
                          Select applicable pain types
                          </FormLabel>
                          <FormGroup row>
                            {painTypes?.map((painType, index) => (
                              <FormControlLabel
                                key={index}
                                control={
                                  <Checkbox
                                    checked={painType.selected}
                                    onChange={(e) => {
                                      const newConditions = [...painTypes];
                                      newConditions[index].selected = e.target.checked;
                                      setPainTypes(newConditions);
                                    }}
                                  />
                                }
                                label={painType.displayValue}
                              />
                            ))}
                          </FormGroup>
                        </FormControl>
                      </Grid2>
                      <Grid2 size={12}>
                        <TextField
                          id="outlined-multiline-static"
                          label="Range of Motion"
                          multiline
                          rows={3}
                          fullWidth
                          value={rangeOfMotion}
                          onChange={(e) => setRangeOfMotion(e.target.value)}
                          slotProps={{ inputLabel: { shrink: rangeOfMotion?true:false} }}
                        />
                      </Grid2>
                      <Grid2 size={12}>
                        <TextField
                          id="outlined-multiline-static"
                          label="Muscle Strength"
                          multiline
                          rows={3}
                          fullWidth
                          value={muscleStrength}
                          onChange={(e) => setMuscleStrength(e.target.value)}
                          slotProps={{ inputLabel: { shrink: muscleStrength?true:false} }}
                        />
                      </Grid2>
                      <Grid2 size={12}>
                        <TextField
                          id="outlined-multiline-static"
                          label="Muscle spasm \ Tightness \ Trigger points"
                          multiline
                          rows={3}
                          fullWidth
                          value={muscleTightness}
                          onChange={(e) => setMuscleTightness(e.target.value)}
                          slotProps={{ inputLabel: { shrink: muscleTightness?true:false} }}
                        />
                      </Grid2>
                      <Grid2 size={12}>
                        <TextField
                          id="outlined-multiline-static"
                          label="Special Test"
                          multiline
                          rows={3}
                          fullWidth
                          value={specialTests}
                          onChange={(e) => setSpecialTests(e.target.value)}
                          slotProps={{ inputLabel: { shrink: specialTests?true:false} }}
                        />
                      </Grid2>
                    </Grid2>
                  </AccordionDetails>
                </Accordion>

                <Accordion defaultExpanded variant="outlined" sx={{ width: "100%", maxWidth: "100%" }}>
                  <AccordionSummary
                    expandIcon={<GridExpandMoreIcon />}
                    aria-controls="panel3-content"
                    id="panel3-header"
                  >
                    <Typography component="span">Prescribed Treatment</Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ padding: "8px", position: "relative" }}>
                    <Grid2 container spacing={3} size={12} sx={{justifyContent:'center', paddingX:'10px'}}>
                      <Grid2 size={12}>
                        <TextField
                          id="outlined-multiline-static"
                          label="Treatment Plan"
                          multiline
                          rows={5}
                          fullWidth
                          value={treatmentPlan}
                          onChange={(e) => setTreatmentPlan(e.target.value)}
                          slotProps={{ inputLabel: { shrink: treatmentPlan?true:false} }}
                        />
                      </Grid2>
                      <Grid2 size={12}>
                        <TextField
                          id="outlined-multiline-static"
                          type="number"
                          defaultValue="0"
                          label="Suggested no. of sessions"
                          value={suggestedSessions}
                          onChange={(e) => setSuggestedSessions(e.target.value)}
                        />
                      </Grid2>
                      <Grid2 size={12}>
                        <TextField
                          id="outlined-multiline-static"
                          label="Short term treatment goals"
                          multiline
                          rows={4}
                          fullWidth
                          value={shortTermTreatmentGoals}
                          onChange={(e) => setShortTermTreatmentGoals(e.target.value)}
                          slotProps={{ inputLabel: { shrink: shortTermTreatmentGoals?true:false} }}
                        />
                      </Grid2>
                      <Grid2 size={12}>
                        <FormControl>
                          <FormLabel component="legend">
                            Treatment Methods
                          </FormLabel>
                          <FormGroup row>
                            {treatmentMethods?.map((treatment, index) => (
                              <FormControlLabel
                                key={index}
                                control={
                                  <Checkbox
                                    checked={treatment.selected}
                                    onChange={(e) => {
                                      const newConditions = [...treatmentMethods];
                                      newConditions[index].selected = e.target.checked;
                                      setTreatmentMethods(newConditions);
                                      handleOthersCheck(newConditions);
                                    }}
                                  />
                                }
                                label={treatment.displayValue}
                              />
                            ))}
                          </FormGroup>
                          {othersChecked && (
                            <TextField
                              id="outlined-multiline-static"
                              label="Specify Other"
                              value={treatmentMethods[treatmentMethods.findIndex((treatment) => treatment.value === "Others")].additionalText}
                              onChange={(e) => {
                                const newConditions = [...treatmentMethods];
                                newConditions[treatmentMethods.findIndex((treatment) => treatment.value === "Others")].additionalText = e.target.value;
                                setTreatmentMethods(newConditions);
                              }}>
                            </TextField>
                          )}
                        </FormControl>
                      </Grid2>
                      <Grid2 size={12}>
                        <TextField
                          id="outlined-multiline-static"
                          label="Long term treatment goals"
                          multiline
                          rows={4}
                          fullWidth
                          value={longTermTreatmentGoals}
                          onChange={(e) => setLongTermTreatmentGoals(e.target.value)}
                          slotProps={{ inputLabel: { shrink: longTermTreatmentGoals?true:false} }}
                        />
                      </Grid2>
                      <Grid2 size={12}>
                        <TextField
                          id="outlined-multiline-static"
                          label="Do's and Dont's"
                          multiline
                          rows={4}
                          fullWidth
                          value={dosDonts}
                          onChange={(e) => setDosDonts(e.target.value)}
                          slotProps={{ inputLabel: { shrink: dosDonts?true:false} }}
                        />
                      </Grid2>
                    </Grid2>
                  </AccordionDetails>
                </Accordion>
              </Grid2>
            </Card>
          </Grid2>
        </Grid2>
      </Grid2>
    </Box>
  );
}

export default PhysiotherapyPrescriptionForm;